// ErrorBoundary.tsx (один файл)
import React, { Component, ErrorInfo, ReactNode, useCallback } from 'react';
import useMetrics from '@hooks/useMetrics';
import FallbackComponent from 'views/fallbackComponent/FallbackComponent';
import { METRIC_EVENTS, METRIC_EVENTS_TRIGGERS } from '@configs/metrics/events';

type BoundaryProps = {
  children: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
};

type BoundaryState = {
  hasError: boolean;
  errorMessage?: string;
};

// Классовая граница ошибок
class ErrorBoundaryImpl extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info);
    // продублируем в консоль, если нужно
    console.error('Uncaught error:', error, info);
  }

  render() {
    return this.state.hasError ? (
      <FallbackComponent errorMessage={this.state.errorMessage} />
    ) : (
      this.props.children
    );
  }
}

// Функциональный экспорт, тут можно использовать хуки
export default function ErrorBoundary({ children }: { children: ReactNode }) {
  const { trackEvent } = useMetrics();

  const handleError = useCallback(
    (error: Error, _info: ErrorInfo) => {
      // поправил опечатку: allert -> alert
      trackEvent(METRIC_EVENTS.error_screen_alert, METRIC_EVENTS_TRIGGERS.main, {
        description: 'Error occurred',
        error_code: error.name || 'unknown',
        error_text: error.message || 'unknown',
      });
    },
    [trackEvent],
  );

  return <ErrorBoundaryImpl onError={handleError}>{children}</ErrorBoundaryImpl>;
}
